import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useMachineSetupStore } from '../../stores/machine-setup-store';
import { useOperatorLocationPicker } from '../../hooks/useOperatorLocationPicker';
import { SearchableSelect } from '../molecules/SearchableSelect';
import type { SearchableSelectOption } from '../molecules/SearchableSelect';
import type { SerialValidationResponse, WizardData, DeviceSuggestion } from '../../types/machine-setup-types';

interface MachineDetailsStepProps {
  wizardData: Partial<WizardData>;
  serialValidation: SerialValidationResponse | null;
  isLoading: boolean;
  onChange: (data: Partial<WizardData>) => void;
  onValidateSerial: (serial: string) => void;
}

export function MachineDetailsStep({
  wizardData,
  serialValidation,
  isLoading,
  onChange,
  onValidateSerial,
}: MachineDetailsStepProps) {
  const [nameError, setNameError] = useState('');

  // ── Operator & Location picker (shared hook) ────────────────────────────────
  const {
    operators, selectedOperator, setSelectedOperator,
    locations, setLocations, locationsLoading, selectedLocation, setSelectedLocation,
    setOperatorQuery, setLocationQuery,
  } = useOperatorLocationPicker();

  const operatorOptions: SearchableSelectOption[] = useMemo(
    () => operators.map((o) => ({ id: String(o.operatorId), label: o.name, subLabel: `ID: ${o.operatorId}` })),
    [operators],
  );

  const locationOptions: SearchableSelectOption[] = useMemo(
    () => locations.map((l) => ({ id: String(l.actorId), label: l.name, subLabel: `ID: ${l.actorId}` })),
    [locations],
  );

  // ── Serial autocomplete ──────────────────────────────────────────────────────
  const [deviceSuggestions, setDeviceSuggestions] = useState<DeviceSuggestion[]>([]);
  const [serialOpen, setSerialOpen] = useState(false);
  const serialRef = useRef<HTMLDivElement>(null);

  const { loadDevicesForActor } = useMachineSetupStore();

  // Pre-fill on back-navigation: restore operator + location from wizardData
  useEffect(() => {
    if (!wizardData.actorId || operators.length === 0) return;
    if (selectedOperator) return; // already initialised

    // Try to match the saved actorId as an operator directly
    const op = operators.find((o) => o.operatorId === wizardData.actorId);
    if (op) {
      setSelectedOperator(op);
      setOperatorQuery(op.name);
    } else if (wizardData.actorName) {
      // actorName = "OperatorName › LocationName" — extract operator name
      const parts = wizardData.actorName.split(' › ');
      if (parts.length >= 2) {
        const opName = parts[0];
        const found = operators.find((o) => o.name === opName);
        if (found) {
          setSelectedOperator(found);
          setOperatorQuery(found.name);
          // location will be set once locations load (via second useEffect below)
        }
      }
    }
  }, [wizardData.actorId, operators]); // eslint-disable-line react-hooks/exhaustive-deps

  // After locations load, restore the saved location if actorId != operator
  useEffect(() => {
    if (!wizardData.actorId || !selectedOperator || locations.length === 0) return;
    if (wizardData.actorId === selectedOperator.operatorId) return; // actorId is the operator itself
    if (selectedLocation) return; // already set

    const loc = locations.find((l) => l.actorId === wizardData.actorId);
    if (loc) {
      setSelectedLocation(loc);
      setLocationQuery(loc.name);
    }
  }, [locations, wizardData.actorId, selectedOperator?.operatorId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch devices for the whole operator — populates serial autocomplete
  useEffect(() => {
    const operatorId = selectedOperator?.operatorId ?? 0;
    if (operatorId <= 0) { setDeviceSuggestions([]); return; }
    loadDevicesForActor(operatorId)
      .then(setDeviceSuggestions)
      .catch((err) => {
        console.warn('Failed to load devices for actor:', err);
        setDeviceSuggestions([]);
      });
  }, [selectedOperator?.operatorId, loadDevicesForActor]);

  // Close serial dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (serialRef.current && !serialRef.current.contains(e.target as Node)) setSerialOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const serial = wizardData.serialNumber ?? '';

  // Debounced auto-validation — fires 600 ms after the user stops typing manually.
  // Timer is stored in a ref so it persists across renders and can always be cancelled.
  // onValidateSerialRef keeps the latest callback without invalidating the debounce closure.
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();
  const onValidateSerialRef = useRef(onValidateSerial);
  useEffect(() => { onValidateSerialRef.current = onValidateSerial; }, [onValidateSerial]);

  const debouncedValidate = useCallback((value: string) => {
    clearTimeout(debounceTimer.current);
    if (!value.trim()) return;
    debounceTimer.current = setTimeout(() => onValidateSerialRef.current(value.trim()), 600);
  }, []);

  const serialStatusBadge = () => {
    if (!serialValidation) return null;
    if (!serialValidation.deviceFound)
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          Device not found in Lynx
        </span>
      );
    if (serialValidation.isWrongDistributor)
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          Wrong distributor
        </span>
      );
    if (serialValidation.isAssigned)
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
          Already assigned to a machine
        </span>
      );
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        Available — ready to assign
      </span>
    );
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Machine Details</h2>
      <p className="text-sm text-gray-500 mb-6">Enter the details for the new machine.</p>
      <div className="space-y-5 max-w-lg">

        {/* Machine name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Machine Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={wizardData.machineName ?? ''}
            onChange={(e) => {
              setNameError(e.target.value.trim() ? '' : 'Machine name is required');
              onChange({ machineName: e.target.value });
            }}
            placeholder="e.g. EV Charger Site A"
            className={[
              'w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring',
              nameError ? 'border-red-400' : 'border-gray-300',
            ].join(' ')}
          />
          {nameError && <p className="mt-1 text-xs text-red-500">{nameError}</p>}
        </div>

        {/* Step 1: Operator */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Operator <span className="text-red-500">*</span>
          </label>
          <SearchableSelect
            options={operatorOptions}
            value={selectedOperator ? String(selectedOperator.operatorId) : undefined}
            onValueChange={(id) => {
              const op = operators.find((o) => o.operatorId === Number(id));
              if (!op) return;
              setSelectedOperator(op);
              setOperatorQuery(op.name);
              onChange({ actorId: op.operatorId, operatorActorId: op.operatorId, actorName: op.name });
            }}
            placeholder="Select an operator…"
            searchPlaceholder="Search by name or operator ID…"
            emptyMessage="No operators found"
            isLoading={operators.length === 0}
            loadingMessage="Loading operators…"
          />
        </div>

        {/* Step 2: Location (shown once an operator is selected) */}
        {selectedOperator && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location <span className="text-gray-400 text-xs">
                {locationsLoading ? '(loading…)' : locations.length === 0 ? '(no sub-locations)' : ''}
              </span>
            </label>
            {locationsLoading ? (
              <div className="flex items-center gap-2 h-10 text-sm text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                Loading locations…
              </div>
            ) : locations.length === 0 ? (
              <p className="text-xs text-gray-500 bg-gray-50 rounded-md px-3 py-2 border border-gray-200">
                No sub-locations found. The machine will be assigned directly to <span className="font-medium">{selectedOperator.name}</span>.
              </p>
            ) : (
              <SearchableSelect
                options={locationOptions}
                value={selectedLocation ? String(selectedLocation.actorId) : undefined}
                onValueChange={(id) => {
                  const loc = locations.find((l) => l.actorId === Number(id));
                  if (!loc) return;
                  setSelectedLocation(loc);
                  setLocationQuery(loc.name);
                  onChange({
                    actorId: loc.actorId,
                    operatorActorId: selectedOperator.operatorId,
                    actorName: `${selectedOperator.name} › ${loc.name}`,
                  });
                }}
                placeholder="Select a location…"
                searchPlaceholder="Search by location name or ID…"
                emptyMessage="No locations found"
              />
            )}
          </div>
        )}

        {/* Device Serial Number (optional) — with autocomplete */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Device Serial Number <span className="text-gray-400 text-xs">(optional)</span>
          </label>
          <div ref={serialRef} className="relative">
              <input
                type="text"
                value={serial}
                onChange={(e) => {
                  onChange({ serialNumber: e.target.value });
                  setSerialOpen(true);
                  debouncedValidate(e.target.value);
                }}
                onFocus={() => setSerialOpen(true)}
                placeholder={deviceSuggestions.length > 0 ? `${deviceSuggestions.length} device${deviceSuggestions.length !== 1 ? 's' : ''} available — type to filter` : 'e.g. NX123456789'}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {serialOpen && deviceSuggestions.length > 0 && (
                <ul className="absolute z-10 mt-1 w-full bg-white rounded-md border border-gray-200 shadow-lg max-h-52 overflow-auto text-sm">
                  {deviceSuggestions
                    .filter(d => !serial || d.deviceSerial.toLowerCase().includes(serial.toLowerCase()))
                    .slice(0, 20)
                    .map(d => (
                      <li
                        key={d.deviceSerial}
                        onMouseDown={() => {
                          clearTimeout(debounceTimer.current);
                          onChange({ serialNumber: d.deviceSerial });
                          setSerialOpen(false);
                          onValidateSerial(d.deviceSerial);
                        }}
                        className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-primary/5"
                      >
                        <span className="font-mono text-gray-900">{d.deviceSerial}</span>
                        {d.boardSerial && (
                          <span className="ml-3 text-xs text-gray-400 font-mono shrink-0">{d.boardSerial}</span>
                        )}
                      </li>
                    ))}
                  {deviceSuggestions.filter(d => !serial || d.deviceSerial.toLowerCase().includes(serial.toLowerCase())).length === 0 && (
                    <li className="px-3 py-2 text-gray-400">No matching devices</li>
                  )}
                </ul>
              )}
            </div>
          {serialValidation && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm text-gray-500">Status:</span>
              {serialStatusBadge()}
            </div>
          )}
          {serialValidation && !serialValidation.deviceFound && (
            <p className="mt-1 text-xs text-red-600 bg-red-50 rounded-md p-2 border border-red-200">
              Serial not found in Lynx. Verify the number or leave blank and assign later.
            </p>
          )}
          {serialValidation?.isWrongDistributor && (
            <p className="mt-1 text-xs text-red-600 bg-red-50 rounded-md p-2 border border-red-200">
              This device belongs to a different distributor and cannot be used here.
            </p>
          )}
          {serialValidation?.isAssigned && !serialValidation.isWrongDistributor && (
            <p className="mt-1 text-xs text-yellow-700 bg-yellow-50 rounded-md p-2 border border-yellow-200">
              This device is already linked to another machine. Choose a different serial or leave blank.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
